import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs/operators';
import { objectContainsTerm } from 'src/app/shared/utils/object-search.util';

import { Comment } from 'src/app/models/comment.model';
import {
  CSVTicket,
  Ticket,
  TicketGrouping,
  TicketGroupingLabel,
  TicketRecordType,
  TicketStatus,
  TicketType,
} from 'src/app/models/ticket/ticket.model';
import {
  getTicketType,
  mapStringToTicketGrouping,
} from 'src/app/models/ticket/ticket.util';
import * as fromStore from 'src/app/store/';

type TicketSortField =
  | 'caseNumber'
  | 'created'
  | 'location'
  | 'status'
  | 'title'
  | 'type';

type TicketSortDirection = 'asc' | 'desc';

type TicketSortKey =
  | 'caseNumber-asc'
  | 'caseNumber-desc'
  | 'created-asc'
  | 'created-desc'
  | 'location-asc'
  | 'location-desc'
  | 'status-asc'
  | 'status-desc'
  | 'title-asc'
  | 'title-desc'
  | 'type-asc'
  | 'type-desc';

interface TicketSortOption {
  direction: TicketSortDirection;
  field: TicketSortField;
  label: string;
  value: TicketSortKey;
}

@Component({
  selector: 'app-ticket-list-display',
  templateUrl: './ticket-list-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketListDisplayComponent implements OnInit, OnDestroy {
  readonly tickets = input<Ticket[]>([]);

  readonly totalTickets = input<number>(0);

  readonly csvData = input<CSVTicket[]>([]);

  @Input() isLoading: boolean;

  readonly canCreateTicket = input<boolean>(true);

  readonly selectTicket = output<Ticket>();

  readonly openCreateTicketDialog = output<void>();

  readonly timeRangeChanged = output<string>();

  validStatusValues = [TicketStatus.Open, TicketStatus.Closed];

  searchTermControl = new UntypedFormControl();

  currentGrouping: TicketGrouping = TicketGrouping.Type;

  TicketGrouping = TicketGrouping;

  groupedTickets: { key: string | TicketRecordType; tickets: Ticket[] }[] = [];

  validTypeValues: string[] = Object.keys(TicketType);

  selectedTicketId = signal<string | null>(null);

  activeStatusFilter: 'all' | 'open' | 'closed' = 'all';

  readonly filteredBySearch = signal<Ticket[]>([]);

  readonly allCount = computed(() => this.filteredBySearch().length);

  readonly openCount = computed(
    () => this.filteredBySearch().filter((t) => !t.IsClosed).length,
  );

  readonly closedCount = computed(
    () => this.filteredBySearch().filter((t) => t.IsClosed).length,
  );

  expandedGroups = signal(new Set<string>());

  isGroupByOpen = signal(false);

  isSortOpen = signal(false);

  isTimeRangeOpen = signal(false);

  selectedSort: TicketSortKey = 'created-desc';

  selectedTimeRange = signal('');

  readonly sortOptions: TicketSortOption[] = [
    {
      value: 'created-desc',
      label: 'Newest first',
      field: 'created',
      direction: 'desc',
    },
    {
      value: 'created-asc',
      label: 'Oldest first',
      field: 'created',
      direction: 'asc',
    },
    {
      value: 'type-asc',
      label: 'Type A-Z',
      field: 'type',
      direction: 'asc',
    },
    {
      value: 'type-desc',
      label: 'Type Z-A',
      field: 'type',
      direction: 'desc',
    },
    {
      value: 'status-asc',
      label: 'Status A-Z',
      field: 'status',
      direction: 'asc',
    },
    {
      value: 'status-desc',
      label: 'Status Z-A',
      field: 'status',
      direction: 'desc',
    },
    {
      value: 'location-asc',
      label: 'Location A-Z',
      field: 'location',
      direction: 'asc',
    },
    {
      value: 'location-desc',
      label: 'Location Z-A',
      field: 'location',
      direction: 'desc',
    },
    {
      value: 'caseNumber-asc',
      label: 'Ticket # ASC',
      field: 'caseNumber',
      direction: 'asc',
    },
    {
      value: 'caseNumber-desc',
      label: 'Ticket # DESC',
      field: 'caseNumber',
      direction: 'desc',
    },
    {
      value: 'title-asc',
      label: 'Title A-Z',
      field: 'title',
      direction: 'asc',
    },
    {
      value: 'title-desc',
      label: 'Title Z-A',
      field: 'title',
      direction: 'desc',
    },
  ];

  readonly timeRangeOptions = [
    { value: '', label: 'Any date' },
    { value: '6hours', label: 'Last 6 hours' },
    { value: '1day', label: 'Last 24 hours' },
    { value: '1month', label: 'Last 30 days' },
    { value: '3months', label: 'Last 90 days' },
    { value: 'olderThan30', label: 'Older than 30 days' },
  ];

  get currentGroupByLabel(): string {
    return TicketGrouping[this.currentGrouping] ?? 'Type';
  }

  get currentSortLabel(): string {
    return (
      this.sortOptions.find((option) => option.value === this.selectedSort)
        ?.label ?? 'Newest first'
    );
  }

  get currentTimeRangeLabel(): string {
    return (
      this.timeRangeOptions.find((o) => o.value === this.selectedTimeRange())
        ?.label ?? 'Any date'
    );
  }

  get hasActiveFilters(): boolean {
    return (
      this.activeStatusFilter !== 'all' ||
      !!this.searchTerm ||
      !!this.selectedTimeRange() ||
      this.selectedSort !== 'created-desc' ||
      this.currentGrouping !== TicketGrouping.Type
    );
  }

  get resultsSummary(): string {
    const count = this.filteredTotal() || 0;
    const ticketLabel = count === 1 ? 'ticket' : 'tickets';
    const statusLabel =
      this.activeStatusFilter === 'all' ? '' : `${this.activeStatusFilter} `;

    return `Showing ${count} ${statusLabel}${ticketLabel}  •  Grouped by ${this.currentGroupByLabel}  •  Sort: ${this.currentSortLabel}  •  ${this.currentTimeRangeLabel}`;
  }

  @HostListener('document:click')
  closeAllDropdowns(): void {
    if (this.isGroupByOpen() || this.isSortOpen() || this.isTimeRangeOpen()) {
      this.isGroupByOpen.set(false);
      this.isSortOpen.set(false);
      this.isTimeRangeOpen.set(false);
    }
  }

  searchTerm = '';

  readonly filteredTicketsByParams = signal<Ticket[]>([]);

  filteredTickets: Ticket[] = [];

  readonly filteredTotal = signal<number>(0);

  private activatedRoute = inject(ActivatedRoute);

  private route = inject(Router);

  private cdr = inject(ChangeDetectorRef);

  private store = inject(Store<fromStore.State>);

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        // If no groupBy parameter is provided, navigate with default (preserve other params)
        if (!params.groupBy) {
          this.route.navigate([], {
            queryParams: { groupBy: TicketGroupingLabel.Type },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
          return;
        }

        // Sync filter state from URL params (applied before applyFilters so the pipeline uses them)
        const { view } = params;
        if (view && ['all', 'open', 'closed'].includes(view)) {
          this.activeStatusFilter = view as 'all' | 'open' | 'closed';
        } else if (!view) {
          this.activeStatusFilter = 'all';
        }

        const { sort } = params;
        if (sort && this.sortOptions.some((o) => o.value === sort)) {
          this.selectedSort = sort;
        }

        const { q } = params;
        if (q !== undefined && q !== this.searchTerm) {
          this.searchTerm = q;
          this.searchTermControl.setValue(q, { emitEvent: false });
        }

        const { range } = params;
        if (range !== undefined && range !== this.selectedTimeRange()) {
          this.selectedTimeRange.set(range ?? '');
          this.timeRangeChanged.emit(range ?? '');
        }

        const ticketId = params.ticket;
        if (ticketId && ticketId !== this.selectedTicketId()) {
          this.selectedTicketId.set(ticketId); // set immediately so applySearch doesn't clobber it
          this.store.dispatch(new fromStore.SetSelectedTicket(ticketId));
        }

        if (params.olderThan) {
          if (this.selectedTimeRange() !== 'olderThan30') {
            this.selectedTimeRange.set('olderThan30');
          }
        } else if (this.selectedTimeRange() === 'olderThan30') {
          this.selectedTimeRange.set(params.range ?? '');
        }

        if (params.type && this.expandedGroups().size === 0) {
          this.expandedGroups.update((set) => {
            const next = new Set(set);
            next.add(params.type);
            return next;
          });
        }

        this.cdr.markForCheck();
        this.applyFilters(params);
      });

    this.searchTermControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value: string) => {
        this.searchTerm = (value ?? '').trim();
        this.cdr.markForCheck();
        this.syncToUrl({ q: this.searchTerm || null });
        this.applySearch();
      });

    this.store
      .select(fromStore.getSelectedTicket)
      .pipe(
        takeUntil(this.destroy$),
        map((t) => t?.Id ?? null),
        distinctUntilChanged(),
      )
      .subscribe((id) => {
        this.selectedTicketId.set(id);
      });
  }

  private ticketsEffect = effect(() => {
    this.tickets(); // track ONLY this signal
    untracked(() =>
      this.applyFilters(this.activatedRoute.snapshot.queryParams),
    );
  });

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilters(params: {
    groupBy?: TicketGroupingLabel;
    olderThan?: string;
    last?: string;
  }): void {
    if (!this.tickets()?.length) {
      this.groupedTickets = [];
      this.filteredTicketsByParams.set([]);
      this.filteredBySearch.set([]);
      this.filteredTickets = [];
      this.filteredTotal.set(0);
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.groupedTickets = [];

    // Default to 'Type' if no groupBy parameter is provided
    const groupByParam = params.groupBy || TicketGroupingLabel.Type;
    const groupBy = TicketGroupingLabel[groupByParam];
    if (!groupBy?.length) {
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.currentGrouping = mapStringToTicketGrouping(groupBy);
    this.cdr.markForCheck();

    let filtered = [...this.tickets()];

    if (params.olderThan) {
      const olderthan = parseInt(params.olderThan, 10);
      filtered = filtered.filter(
        (t) => moment().diff(t.CreatedDate, 'days') > olderthan,
      );
    }

    if (params.last) {
      const last = parseInt(params.last, 10);
      filtered = filtered.filter(
        (t) => moment().diff(t.CreatedDate, 'days') <= last,
      );
    }

    // Save param-filtered baseline
    this.filteredTicketsByParams.set(filtered);

    // 3) Now apply the text search + regroup
    this.applySearch();
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  openDialog(): void {
    this.openCreateTicketDialog.emit();
  }

  onTicketRowClick(ticket: Ticket): void {
    if (!ticket?.Id) {
      return;
    }

    this.onSelectTicket(ticket);
  }

  onSelectTicket($event: Ticket, syncUrl = true): void {
    if (!$event?.Id) {
      this.store.dispatch(new fromStore.SetSelectedTicket(null));
      this.selectedTicketId.set(null);
      this.selectTicket.emit(null);
      if (syncUrl) this.syncToUrl({ ticket: null });
      return;
    }

    this.selectedTicketId.set($event?.Id);
    this.store.dispatch(new fromStore.SetSelectedTicket($event?.Id));
    this.selectTicket.emit($event);
    if (syncUrl) this.syncToUrl({ ticket: $event.Id });
  }

  hasUnreadComments = (comments: Comment[]): boolean => {
    if (comments.length) {
      return comments.some((c) => !c.Is_Read__c);
    }
    return false;
  };

  onClickGroupBy(value: string): void {
    const grouping = mapStringToTicketGrouping(value);
    if (this.currentGrouping === grouping) {
      return;
    }

    this.currentGrouping = grouping;
    this.cdr.markForCheck();
    this.route.navigate([], {
      queryParams: { groupBy: value },
      queryParamsHandling: 'merge',
    });
    this.groupTickets(this.currentGrouping, this.tickets());
  }

  groupTickets(grouping: TicketGrouping, currTickets?: Ticket[]): void {
    switch (grouping) {
      case TicketGrouping.Type:
        this.groupTicketsByType(currTickets);
        break;
      case TicketGrouping.Status:
        this.groupTicketsByStatus(currTickets);
        break;
      case TicketGrouping.Location:
        this.groupTicketsByLocation(currTickets);
        break;
      default:
        // In case new groupings are added but not handled
        this.groupTicketsByType(currTickets);
        break;
    }
    this.initExpansion();
    this.applySelectedSort();
  }

  groupTicketsByType(tickets: Ticket[]): void {
    this.groupedTickets = [];
    const careTickets = {
      key: TicketRecordType.Care,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Care,
      ),
    };
    const financeTickets = {
      key: TicketRecordType.Finance,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Finance,
      ),
    };
    const maintenanceTickets = {
      key: TicketRecordType.Maintenance,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Maintenance,
      ),
    };
    const proactiveTickets = {
      key: TicketRecordType.Proactive,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Proactive,
      ),
    };
    const supportTickets = {
      key: TicketRecordType.Support,
      tickets: tickets.filter(
        (ticket) => getTicketType(ticket)[0] === TicketRecordType.Support,
      ),
    };

    if (careTickets.tickets.length > 0) {
      this.groupedTickets.push(careTickets);
    }
    if (financeTickets.tickets.length > 0) {
      this.groupedTickets.push(financeTickets);
    }
    if (maintenanceTickets.tickets.length > 0) {
      this.groupedTickets.push(maintenanceTickets);
    }
    if (proactiveTickets.tickets.length > 0) {
      this.groupedTickets.push(proactiveTickets);
    }
    if (supportTickets.tickets.length > 0) {
      this.groupedTickets.push(supportTickets);
    }
  }

  groupTicketsByStatus(tickets: Ticket[]): void {
    this.groupedTickets = [];
    const allStatuses = new Map();
    allStatuses.set(TicketStatus.Open, { key: TicketStatus.Open, tickets: [] });
    allStatuses.set(TicketStatus.Closed, {
      key: TicketStatus.Closed,
      tickets: [],
    });

    tickets.forEach((ticket) => {
      const groupName = ticket.IsClosed
        ? TicketStatus.Closed
        : TicketStatus.Open;
      allStatuses.get(groupName).tickets.push(ticket);
    });
    if (allStatuses.get(TicketStatus.Open).tickets.length > 0) {
      this.groupedTickets.push(allStatuses.get(TicketStatus.Open));
    }
    if (allStatuses.get(TicketStatus.Closed).tickets.length > 0) {
      this.groupedTickets.push(allStatuses.get(TicketStatus.Closed));
    }
  }

  groupTicketsByLocation(tickets: Ticket[]): void {
    this.groupedTickets = [];
    // get all locations that there could be
    const locations = new Set(
      this.tickets().map((ticket) => ticket.Service_Location_Address__c),
    );
    // loop through all the addresses, and create a group for them
    locations.forEach((location) => {
      const locationGroup = {
        key: location,
        tickets: tickets.filter(
          (ticket) => ticket.Service_Location_Address__c === location,
        ),
      };
      if (locationGroup.tickets.length > 0) {
        this.groupedTickets.push(locationGroup);
      }
    });

    this.groupedTickets = this.groupedTickets.sort((statusA, statusB) => {
      if (statusA.key < statusB.key) {
        return -1;
      }
      if (statusA.key > statusB.key) {
        return 1;
      }
      return 0;
    });
  }

  selectStatusFilter(filter: 'all' | 'open' | 'closed'): void {
    // Close any open dropdowns first so the @HostListener document:click
    // handler is a no-op when it fires, preventing a mid-click re-render.
    this.isGroupByOpen.set(false);
    this.isSortOpen.set(false);
    this.isTimeRangeOpen.set(false);
    this.activeStatusFilter = filter;
    this.cdr.markForCheck();
    this.syncToUrl({ view: filter === 'all' ? null : filter });
    this.applySearch();
  }

  clearFilters(): void {
    const shouldEmitTimeRangeChange = !!this.selectedTimeRange();

    this.activeStatusFilter = 'all';
    this.selectedTimeRange.set('');
    this.selectedSort = 'created-desc';
    this.currentGrouping = TicketGrouping.Type;
    this.searchTerm = '';
    this.searchTermControl.setValue('', { emitEvent: false });

    if (shouldEmitTimeRangeChange) {
      this.timeRangeChanged.emit('');
    }

    this.route.navigate([], {
      queryParams: {
        groupBy: TicketGroupingLabel.Type,
        view: null,
        sort: null,
        range: null,
        q: null,
        ticket: null,
        olderThan: null,
      },
      queryParamsHandling: 'merge',
    });
    this.applySearch();
  }

  openGroupByDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isGroupByOpen.update((v) => !v);
    this.isSortOpen.set(false);
    this.isTimeRangeOpen.set(false);
  }

  selectGroupBy(value: string): void {
    this.isGroupByOpen.set(false);
    this.onClickGroupBy(value);
  }

  openSortDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isSortOpen.update((v) => !v);
    this.isGroupByOpen.set(false);
    this.isTimeRangeOpen.set(false);
  }

  selectSort(sort: TicketSortKey | boolean): void {
    this.isSortOpen.set(false);
    if (typeof sort === 'boolean') {
      this.selectedSort = sort ? 'created-desc' : 'created-asc';
    } else {
      this.selectedSort = sort;
    }
    this.syncToUrl({
      sort: this.selectedSort === 'created-desc' ? null : this.selectedSort,
    });
    this.applySelectedSort();
    this.cdr.markForCheck();
  }

  openTimeRangeDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isTimeRangeOpen.update((v) => !v);
    this.isGroupByOpen.set(false);
    this.isSortOpen.set(false);
  }

  selectTimeRange(value: string): void {
    this.isTimeRangeOpen.set(false);
    this.selectedTimeRange.set(value);
    this.cdr.markForCheck();
    if (value === 'olderThan30') {
      this.syncToUrl({ range: null, olderThan: '30' });
      this.timeRangeChanged.emit('');
    } else {
      this.syncToUrl({ range: value || null, olderThan: null });
      this.timeRangeChanged.emit(value);
    }
  }

  sortTicketsWithinGroups(): void {
    const sortOption = this.getSelectedSortOption();
    this.groupedTickets = this.groupedTickets.map((group) => ({
      ...group,
      tickets: [...group.tickets].sort((a, b) =>
        this.compareTickets(a, b, sortOption),
      ),
    }));
    this.cdr.markForCheck();
  }

  private applySelectedSort(): void {
    this.sortGroupsBySelectedSort();
    this.sortTicketsWithinGroups();
  }

  private sortGroupsBySelectedSort(): void {
    const sortOption = this.getSelectedSortOption();
    if (
      sortOption.field === 'caseNumber' ||
      sortOption.field === 'created' ||
      sortOption.field === 'title'
    ) {
      return;
    }

    this.groupedTickets = [...this.groupedTickets].sort((a, b) =>
      this.compareValues(
        this.getGroupSortValue(a, sortOption),
        this.getGroupSortValue(b, sortOption),
        sortOption.direction,
      ),
    );
    this.cdr.markForCheck();
  }

  private getSelectedSortOption(): TicketSortOption {
    return (
      this.sortOptions.find((option) => option.value === this.selectedSort) ??
      this.sortOptions[0]
    );
  }

  private compareTickets(
    a: Ticket,
    b: Ticket,
    sortOption: TicketSortOption,
  ): number {
    const primaryCompare = this.compareValues(
      this.getTicketSortValue(a, sortOption.field),
      this.getTicketSortValue(b, sortOption.field),
      sortOption.direction,
    );

    if (primaryCompare !== 0) {
      return primaryCompare;
    }

    return this.compareValues(
      this.getTicketSortValue(a, 'created'),
      this.getTicketSortValue(b, 'created'),
      'desc',
    );
  }

  private compareValues(
    a: number | string,
    b: number | string,
    direction: TicketSortDirection,
  ): number {
    const directionMultiplier = direction === 'asc' ? 1 : -1;

    if (typeof a === 'number' && typeof b === 'number') {
      return (a - b) * directionMultiplier;
    }

    const compared = String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    });

    return compared * directionMultiplier;
  }

  private getGroupSortValue(
    group: { key: string | TicketRecordType; tickets: Ticket[] },
    sortOption: TicketSortOption,
  ): number | string {
    if (sortOption.field === 'created') {
      const times = group.tickets.map((ticket) =>
        this.getTicketSortValue(ticket, 'created'),
      ) as number[];

      return sortOption.direction === 'asc'
        ? Math.min(...times)
        : Math.max(...times);
    }

    if (
      (sortOption.field === 'type' &&
        this.currentGrouping === TicketGrouping.Type) ||
      (sortOption.field === 'status' &&
        this.currentGrouping === TicketGrouping.Status) ||
      (sortOption.field === 'location' &&
        this.currentGrouping === TicketGrouping.Location)
    ) {
      return String(group.key ?? '');
    }

    const values = group.tickets.map((ticket) =>
      this.getTicketSortValue(ticket, sortOption.field),
    );

    return [...values].sort((a, b) =>
      this.compareValues(a, b, sortOption.direction),
    )[0];
  }

  private getTicketSortValue(
    ticket: Ticket,
    field: TicketSortField,
  ): number | string {
    switch (field) {
      case 'caseNumber':
        return ticket.CaseNumber ?? '';
      case 'created':
        return ticket.CreatedDate
          ? new Date(ticket.CreatedDate).getTime()
          : Number.NEGATIVE_INFINITY;
      case 'location':
        return this.getTicketMeta(ticket);
      case 'status':
        return this.getStatusLabel(ticket);
      case 'title':
        return ticket.Subject ?? '';
      case 'type':
        return getTicketType(ticket)[0] ?? TicketRecordType.Other;
      default:
        return '';
    }
  }

  getGroupConfig(key: string | TicketRecordType): {
    icon: string;
    iconClass: string;
    label: string;
    context?: string;
  } {
    switch (key) {
      case TicketRecordType.Care:
        return {
          icon: 'info',
          iconClass: 'text-brand-text',
          label: 'General Inquiry',
        };
      case TicketRecordType.NOC:
        return {
          icon: 'warning_amber',
          iconClass: 'text-brand-text',
          label: 'NOC',
        };
      case TicketRecordType.Finance:
        return {
          icon: 'receipt_long',
          iconClass: 'text-brand-text',
          label: 'Billing & Payment',
        };
      case TicketRecordType.Maintenance:
        return {
          icon: 'build',
          iconClass: 'text-brand-text',
          label: 'Maintenance',
        };
      case TicketRecordType.Proactive:
        return {
          icon: 'campaign',
          iconClass: 'text-brand-text',
          label: 'Proactive',
        };
      case TicketRecordType.Support:
        return {
          icon: 'warning_amber',
          iconClass: 'text-brand-text',
          label: 'Support',
          context: 'NOC',
        };
      case TicketStatus.Open:
        return {
          icon: 'pending',
          iconClass: 'text-brand-text',
          label: 'Open',
        };
      case TicketStatus.Closed:
        return {
          icon: 'check_circle',
          iconClass: 'text-brand-text',
          label: 'Closed',
        };
      case TicketStatus.New:
        return {
          icon: 'fiber_new',
          iconClass: 'text-brand-text',
          label: 'New',
        };
      case TicketStatus.Pending:
        return {
          icon: 'hourglass_empty',
          iconClass: 'text-brand-text',
          label: 'Pending',
        };
      default: {
        const label =
          key && key !== 'null' && key !== 'undefined'
            ? String(key)
            : 'No location on file';
        const icon =
          label === 'No location on file' ? 'location_off' : 'location_on';
        return {
          icon,
          iconClass: 'text-brand-text',
          label,
        };
      }
    }
  }

  toggleGroup(key: string | TicketRecordType): void {
    const k = String(key);
    this.expandedGroups.update((set) => {
      const next = new Set(set);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }

  isGroupExpanded(key: string | TicketRecordType): boolean {
    return this.expandedGroups().has(String(key));
  }

  initExpansion(): void {
    const surviving = new Set(
      [...this.expandedGroups()].filter((k) =>
        this.groupedTickets.some((g) => String(g.key) === k),
      ),
    );
    if (surviving.size === 0 && this.groupedTickets.length > 0) {
      surviving.add(String(this.groupedTickets[0].key));
    }
    this.expandedGroups.set(surviving);
  }

  getDotColor(ticket: Ticket): string {
    return ticket.IsClosed ? 'bg-green-700' : 'bg-red-600';
  }

  getTicketMeta(ticket: Ticket): string {
    return ticket.Service_Location_Address__c || ticket.Service__r?.Name || '';
  }

  isFinanceTicket(ticket: Ticket): boolean {
    return getTicketType(ticket)[0] === TicketRecordType.Finance;
  }

  getStatusLabel(ticket: Ticket): string {
    return ticket.IsClosed ? 'Closed' : 'Open';
  }

  getRelativeTime(date: Date | string): string {
    if (!date) return '';
    return moment(date).fromNow();
  }

  getTicketAriaLabel(ticket: Ticket): string {
    const parts = [
      `Ticket ${ticket.CaseNumber || 'without case number'}`,
      ticket.Subject,
      this.getStatusLabel(ticket),
      this.getTicketMeta(ticket),
      this.getRelativeTime(
        ticket.IsClosed ? ticket.ClosedDate : ticket.CreatedDate,
      ),
    ].filter(Boolean);

    return parts.join(', ');
  }

  private syncToUrl(patch: Record<string, string | null>): void {
    this.route.navigate([], {
      queryParams: patch,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private applySearch(): void {
    const searchTerm = (this.searchTerm || '').trim().toLowerCase();
    const isStatusOpen = searchTerm === 'open';
    const isStatusClosed = searchTerm === 'closed';
    const fields = [
      'CaseNumber',
      'Service_Location_Address__c',
      'Service__r.Name',
      'Service__r.Bandwidth_Upstream__c',
      'Service__r.RecordType.Name',
      'Service__r.Vendor__r.Name',
      'Subject',
      'Comments__r.records.Body__c',
    ];

    const baseline = this.filteredTicketsByParams();

    const afterSearch = (() => {
      if (!searchTerm) return [...baseline];
      if (isStatusOpen) return baseline.filter((ticket) => !ticket.IsClosed);
      if (isStatusClosed) return baseline.filter((ticket) => ticket.IsClosed);
      return baseline.filter((ticket) =>
        objectContainsTerm(ticket, searchTerm, fields),
      );
    })();

    this.filteredBySearch.set(afterSearch);
    this.filteredTickets = [...afterSearch];

    if (this.activeStatusFilter === 'open') {
      this.filteredTickets = this.filteredTickets.filter((t) => !t.IsClosed);
    } else if (this.activeStatusFilter === 'closed') {
      this.filteredTickets = this.filteredTickets.filter((t) => t.IsClosed);
    }

    this.cdr.markForCheck();
    this.groupTickets(this.currentGrouping, this.filteredTickets);
    this.filteredTotal.set(this.filteredTickets.length);

    const selectionVisible =
      this.selectedTicketId() &&
      this.filteredTickets.some((t) => t.Id === this.selectedTicketId());

    if (!selectionVisible) {
      if (this.groupedTickets.length > 0) {
        this.onSelectTicket(this.groupedTickets[0].tickets[0], false);
      } else {
        this.onSelectTicket(null, false);
      }
    }
  }
}
